import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './AddEvidencePopup.module.css';
import LoadingIcon from '../LoadingIcon/LoadingIcon';
import Button from '../Button/Button';
import { ChevronIcon, FolderIcon } from '../../../Routes/Library/LibraryIcons';
import LibraryExplorerTree from './LibraryExplorerTree';
import LibraryFolderPicker from './LibraryFolderPicker';
import Popup from '../Popup/Popup';
import {
  LibraryFileRef,
  LibraryFolder,
  LibraryPortfolio,
  CreateFileLinksFailure,
  dedupeLibraryFileRefs,
  findLibraryFolder,
  flattenLibraryFolders,
  libraryFileRefKey,
} from '../../../Types/Library/Library';
import { createFileLinks, getLibraryPickerPortfolios } from '../../../API/Library/Library';
import {
  deleteConversationFile,
  EvaluateConversationFile,
  getConversationFiles,
  getEvaluateConversationFiles,
  saveConversationFileToLibrary,
} from '../../../API/ConversationFiles/ConversationFiles';
import { getFileIcon } from '../../../Helpers/fileIcon';
import {
  ALLOWED_UPLOAD_ACCEPT,
  ALLOWED_UPLOAD_LABEL,
  rejectedFilesToast,
  splitAllowedFiles,
  toFileList,
} from '../../../Helpers/uploadFileTypes';
import { toast } from 'react-toastify';
import axios from 'axios';

export interface AddEvidenceLinkContext {
  areaId: string;
  questionId: string;
  linkType?: string;
}

export type SavedToLibraryItem = {
  uploadedFileId: string;
  libraryRef: LibraryFileRef;
  linkId?: string;
};

export interface AddEvidencePopupProps extends AddEvidenceLinkContext {
  clientId: string;
  userSub: string;
  contextLabel?: string;
  linkedFileRefs?: LibraryFileRef[];
  onEvidenceAdded?: (linkedRefs: LibraryFileRef[]) => void;
  onUploadedFilesSelected?: (fileIds: string[]) => void;
  onUploadedFilesChanged?: () => void;
  onSavedToLibrary?: (saved: SavedToLibraryItem[]) => void;
  onUploadConversationFile?: (fileList: FileList | null) => Promise<void>;
  uploadingConversationFile?: boolean;
  /** List uploads for this conversation instead of the area/criteria context (Analyse chats). */
  uploadsConversationId?: string;
  /** Hide conversation uploads and only link existing portfolio files. */
  portfolioFilesOnly?: boolean;
  subjectNoun?: string;
  onClose: () => void;
}

type SelectableLibraryFile = LibraryFileRef & {
  portfolioTitle: string;
  folderTitle: string;
  alreadyLinked: boolean;
};

type LinkedEvidenceFile = LibraryFileRef & {
  portfolioTitle?: string;
  folderTitle?: string;
};

const folderPath = (folders: LibraryFolder[] | undefined, folderId: string): string[] | null => {
  for (const folder of folders ?? []) {
    if (folder.Id === folderId) return [folder.Id];
    const nested = folderPath(folder.folders, folderId);
    if (nested) return [folder.Id, ...nested];
  }
  return null;
};

const formatUploadDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const AddEvidencePopup: React.FC<AddEvidencePopupProps> = ({
  areaId,
  questionId,
  linkType = 'Evaluate',
  clientId,
  userSub,
  contextLabel,
  linkedFileRefs = [],
  onEvidenceAdded,
  onUploadedFilesSelected,
  onUploadedFilesChanged,
  onSavedToLibrary,
  onUploadConversationFile,
  uploadingConversationFile = false,
  uploadsConversationId,
  portfolioFilesOnly = false,
  subjectNoun = "criteria",
  onClose,
}) => {
  const [portfolios, setPortfolios] = useState<LibraryPortfolio[]>([]);
  const [loadingPortfolios, setLoadingPortfolios] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [revealKey, setRevealKey] = useState('');
  const [selectedFileKeys, setSelectedFileKeys] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const conversationUploadInputRef = useRef<HTMLInputElement>(null);

  const [uploadedFiles, setUploadedFiles] = useState<EvaluateConversationFile[]>([]);
  const [loadingUploadedFiles, setLoadingUploadedFiles] = useState(true);
  const [uploadedFilesError, setUploadedFilesError] = useState<string | null>(null);
  const [selectedUploadedFileIds, setSelectedUploadedFileIds] = useState<string[]>([]);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [saveTargetIds, setSaveTargetIds] = useState<string[]>([]);
  const [savingToLibrary, setSavingToLibrary] = useState(false);
  const [justUploaded, setJustUploaded] = useState<{ id: string; name: string } | null>(null);

  const linkedKeys = useMemo(
    () => new Set(dedupeLibraryFileRefs(linkedFileRefs).map(libraryFileRefKey)),
    [linkedFileRefs]
  );

  const totalSelected = selectedFileKeys.length + selectedUploadedFileIds.length;

  const loadUploadedFiles = useCallback(async () => {
    setLoadingUploadedFiles(true);
    setUploadedFilesError(null);
    try {
      const data = uploadsConversationId
        ? await getConversationFiles(uploadsConversationId, clientId)
        : await getEvaluateConversationFiles(areaId, questionId, clientId);
      const uniqueFiles = data.filter(
        (file, index, all) => all.findIndex((candidate) => candidate.fileId === file.fileId) === index
      );
      setUploadedFiles(uniqueFiles);
      return uniqueFiles;
    } catch {
      setUploadedFilesError('Failed to load uploaded files. Please try again.');
      setUploadedFiles([]);
      return [];
    } finally {
      setLoadingUploadedFiles(false);
    }
  }, [areaId, questionId, clientId, uploadsConversationId]);

  useEffect(() => {
    if (portfolioFilesOnly) {
      setLoadingUploadedFiles(false);
      setUploadedFiles([]);
      return;
    }
    loadUploadedFiles();
  }, [loadUploadedFiles, portfolioFilesOnly]);

  const reloadPortfolios = useCallback(async () => {
    const data = await getLibraryPickerPortfolios(clientId, userSub);
    setPortfolios(data);
    return data;
  }, [clientId, userSub]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoadingPortfolios(true);
      setLoadError(null);
      try {
        const data = await reloadPortfolios();
        if (cancelled) return;
        if (data.length > 0) {
          setSelectedPortfolioId(data[0].id);
          const firstFolder = data[0].folders?.[0];
          setSelectedFolderId(firstFolder?.Id ?? '');
          const open = new Set<string>([`p:${data[0].id}`]);
          if (firstFolder?.Id) open.add(`f:${firstFolder.Id}`);
          setExpanded(open);
        }
      } catch {
        if (!cancelled) {
          setLoadError('Failed to load your library. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoadingPortfolios(false);
        }
      }
    };

    if (clientId && userSub) {
      load();
    }

    return () => {
      cancelled = true;
    };
  }, [clientId, userSub, reloadPortfolios]);

  const selectedPortfolio = useMemo(
    () => portfolios.find((p) => p.id === selectedPortfolioId),
    [portfolios, selectedPortfolioId]
  );

  const resolvePortfolioFolderTitles = useCallback(
    (portfolioId: string, folderId: string) => {
      const portfolio = portfolios.find((p) => p.id === portfolioId);
      const folder = findLibraryFolder(portfolio?.folders, folderId);
      return {
        portfolioTitle: portfolio?.title,
        folderTitle: folder?.Title,
      };
    },
    [portfolios]
  );

  const linkedEvidenceFiles: LinkedEvidenceFile[] = useMemo(() => {
    return dedupeLibraryFileRefs(linkedFileRefs).map((ref) => {
      const titles = resolvePortfolioFolderTitles(ref.portfolioId, ref.folderId);
      return {
        ...ref,
        portfolioTitle: titles.portfolioTitle,
        folderTitle: titles.folderTitle,
      };
    });
  }, [linkedFileRefs, resolvePortfolioFolderTitles]);

  const libraryFiles: SelectableLibraryFile[] = useMemo(() => {
    const candidates = portfolios.flatMap((portfolio) =>
      flattenLibraryFolders(portfolio.folders).flatMap((folder) =>
        (folder.Files ?? [])
          .filter((file) => file.Title?.trim())
          .map((file) => {
            const ref = {
              portfolioId: portfolio.id,
              folderId: folder.Id,
              fileName: file.Title.trim(),
              fileId: file.Id,
              portfolioTitle: portfolio.title,
              folderTitle: folder.Title,
            };
            return {
              ...ref,
              alreadyLinked: linkedKeys.has(libraryFileRefKey(ref)),
            };
          })
      )
    );
    return dedupeLibraryFileRefs(candidates);
  }, [portfolios, linkedKeys]);

  const visibleFiles = useMemo(
    () => libraryFiles.filter((file) => file.portfolioId === selectedPortfolioId && file.folderId === selectedFolderId),
    [libraryFiles, selectedPortfolioId, selectedFolderId]
  );

  const allSelectableLibraryRefs = useMemo(() => {
    const fromLibrary = libraryFiles.map(
      ({ portfolioId, folderId, fileName, fileId }) => ({
        portfolioId,
        folderId,
        fileName,
        fileId,
      })
    );
    const fromLinked = linkedEvidenceFiles.map(
      ({ portfolioId, folderId, fileName, fileId }) => ({
        portfolioId,
        folderId,
        fileName,
        fileId,
      })
    );
    return dedupeLibraryFileRefs([...fromLibrary, ...fromLinked]);
  }, [libraryFiles, linkedEvidenceFiles]);

  const toggleFileSelection = (key: string) => {
    setSelectedFileKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleUploadedFileSelection = (fileId: string) => {
    setSelectedUploadedFileIds((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    );
  };

  const handleAddEvidence = async () => {
    if (totalSelected === 0) return;

    const selectedLibraryRefs = allSelectableLibraryRefs.filter((ref) =>
      selectedFileKeys.includes(libraryFileRefKey(ref))
    );

    const alreadyLinkedRefs = selectedLibraryRefs.filter((ref) =>
      linkedKeys.has(libraryFileRefKey(ref))
    );
    const refsToCreate = selectedLibraryRefs.filter(
      (ref) => !linkedKeys.has(libraryFileRefKey(ref))
    );

    setSaving(true);
    try {
      let newlyLinkedRefs: LibraryFileRef[] = [];
      let libraryFailures: CreateFileLinksFailure[] = [];

      if (refsToCreate.length > 0) {
        const result = await createFileLinks(
          refsToCreate.map((ref) => ({
            fileName: ref.fileName,
            portfolioId: ref.portfolioId,
            folderId: ref.folderId,
            fileId: ref.fileId,
            type: linkType,
            areaId,
            questionId,
          }))
        );

        libraryFailures = result.failures ?? [];
        const failedKeys = new Set(
          libraryFailures
            .filter((failure) => failure.portfolioId && failure.folderId && failure.fileName)
            .map((failure) =>
              libraryFileRefKey({
                portfolioId: failure.portfolioId!,
                folderId: failure.folderId!,
                fileName: failure.fileName!,
              })
            )
        );

        // If the API reported failures without portfolio/folder/fileName, treat
        // unmatched selected refs as failed when created count is short.
        if (failedKeys.size === 0 && result.failed > 0 && result.created < refsToCreate.length) {
          const successKeysFromLinks = new Set(
            (result.links ?? []).map((link) =>
              libraryFileRefKey({
                portfolioId: link.portfolioId,
                folderId: link.folderId,
                fileName: link.fileName,
              })
            )
          );
          if (successKeysFromLinks.size > 0) {
            refsToCreate.forEach((ref) => {
              const key = libraryFileRefKey(ref);
              if (!successKeysFromLinks.has(key)) {
                failedKeys.add(key);
              }
            });
          }
        }

        newlyLinkedRefs = refsToCreate.filter(
          (ref) => !failedKeys.has(libraryFileRefKey(ref))
        );

        // Keep failed selections checked for retry; clear successful ones.
        if (libraryFailures.length > 0) {
          if (failedKeys.size > 0) {
            setSelectedFileKeys((prev) => prev.filter((key) => failedKeys.has(key)));
          }
          // If we can't map failures to keys, leave selection unchanged for retry.
        } else {
          setSelectedFileKeys([]);
        }
      } else {
        setSelectedFileKeys([]);
      }

      const refsForSelection = dedupeLibraryFileRefs([
        ...newlyLinkedRefs,
        ...alreadyLinkedRefs,
      ]);

      if (refsForSelection.length > 0) {
        onEvidenceAdded?.(refsForSelection);
      }

      const uploadedSelectedCount = selectedUploadedFileIds.length;
      if (uploadedSelectedCount > 0) {
        onUploadedFilesSelected?.(selectedUploadedFileIds);
        setSelectedUploadedFileIds([]);
      }

      const parts: string[] = [];
      if (refsForSelection.length > 0) {
        parts.push(
          `${refsForSelection.length} library file${refsForSelection.length === 1 ? '' : 's'}`
        );
      }
      if (uploadedSelectedCount > 0) {
        parts.push(
          `${uploadedSelectedCount} uploaded file${uploadedSelectedCount === 1 ? '' : 's'}`
        );
      }
      if (parts.length > 0) {
        toast.success(`Selected ${parts.join(' and ')} as evidence sources.`);
      }

      if (libraryFailures.length > 0) {
        const failureNames = libraryFailures
          .map((failure) => failure.fileName)
          .filter(Boolean);
        const detail =
          failureNames.length > 0
            ? `: ${failureNames.join(', ')}`
            : '';
        toast.error(
          `Failed to link ${libraryFailures.length} library file${libraryFailures.length === 1 ? '' : 's'}${detail}. Please try again.`
        );
        return;
      }

      onClose();
    } catch (err) {
      let message = 'Failed to add evidence. Please try again.';
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as
          | { failures?: CreateFileLinksFailure[]; error?: string }
          | string
          | undefined;
        if (data && typeof data === 'object') {
          const failures = Array.isArray(data.failures) ? data.failures : [];
          if (failures.length > 0) {
            const failureNames = failures
              .map((failure) => failure.fileName)
              .filter(Boolean);
            message =
              failureNames.length > 0
                ? `Failed to link library files: ${failureNames.join(', ')}. Please try again.`
                : `Failed to link ${failures.length} library file${failures.length === 1 ? '' : 's'}. Please try again.`;
          } else if (typeof data.error === 'string' && data.error.trim()) {
            message = data.error;
          }
        } else if (typeof data === 'string' && data.trim()) {
          message = data;
        }
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUploadedFile = async (fileId: string, fileName: string) => {
    if (!window.confirm(`Remove "${fileName}"?`)) {
      return;
    }

    setDeletingFileId(fileId);
    try {
      await deleteConversationFile(fileId, clientId);
      setUploadedFiles((prev) => prev.filter((f) => f.fileId !== fileId));
      setSelectedUploadedFileIds((prev) => prev.filter((id) => id !== fileId));
      onUploadedFilesChanged?.();
      toast.success('File removed.');
    } catch {
      toast.error('Failed to remove file. Please try again.');
    } finally {
      setDeletingFileId(null);
    }
  };

  const saveErrorMessage = (err: unknown) => {
    if (axios.isAxiosError(err)) {
      const data = err.response?.data as { message?: string; error?: string } | string | undefined;
      if (data && typeof data === 'object') {
        return data.message || data.error || 'Failed to save the file to your library.';
      }
      if (typeof data === 'string' && data.trim()) return data;
    }
    return 'Failed to save the file to your library.';
  };

  const confirmSaveToLibrary = async (portfolioId: string, folderId: string) => {
    const targets = uploadedFiles.filter((file) => saveTargetIds.includes(file.fileId));
    if (targets.length === 0) {
      setSaveTargetIds([]);
      return;
    }

    setSavingToLibrary(true);
    const saved: SavedToLibraryItem[] = [];
    const failures: string[] = [];
    try {
      for (const file of targets) {
        try {
          const result = await saveConversationFileToLibrary(file.fileId, clientId, portfolioId, folderId);
          saved.push({
            uploadedFileId: file.fileId,
            linkId: result.linkId,
            libraryRef: {
              portfolioId: result.portfolioId,
              folderId: result.folderId,
              fileName: result.fileName,
              fileId: result.fileId,
            },
          });
        } catch (err) {
          failures.push(`${file.fileName}: ${saveErrorMessage(err)}`);
        }
      }

      if (saved.length > 0) {
        const savedIds = new Set(saved.map((item) => item.uploadedFileId));
        const savedKeys = saved
          .filter((item) => selectedUploadedFileIds.includes(item.uploadedFileId))
          .map((item) => libraryFileRefKey(item.libraryRef));
        setUploadedFiles((prev) => prev.filter((file) => !savedIds.has(file.fileId)));
        setSelectedUploadedFileIds((prev) => prev.filter((id) => !savedIds.has(id)));
        if (savedKeys.length > 0) {
          setSelectedFileKeys((prev) => Array.from(new Set([...prev, ...savedKeys])));
        }
        if (justUploaded && savedIds.has(justUploaded.id)) {
          setJustUploaded(null);
        }
        setSelectedPortfolioId(portfolioId);
        setSelectedFolderId(folderId);
        try {
          await reloadPortfolios();
        } catch {
          // The file is saved; the explorer refreshes next time the popup opens.
        }
        onUploadedFilesChanged?.();
        onSavedToLibrary?.(saved);
        toast.success(
          saved.length === 1
            ? `Saved ${saved[0].libraryRef.fileName} to your library.`
            : `Saved ${saved.length} files to your library.`
        );
      }

      failures.forEach((message) => toast.error(message));
      const savedIds = new Set(saved.map((item) => item.uploadedFileId));
      setSaveTargetIds((current) => current.filter((id) => !savedIds.has(id)));
    } finally {
      setSavingToLibrary(false);
    }
  };

  const handleConversationUpload = async (fileList: FileList | null) => {
    if (!fileList?.length || !onUploadConversationFile) return;

    const { allowed, rejected } = splitAllowedFiles(fileList);
    rejectedFilesToast(rejected);
    if (!allowed.length) {
      if (conversationUploadInputRef.current) {
        conversationUploadInputRef.current.value = '';
      }
      return;
    }

    const previousIds = new Set(uploadedFiles.map((f) => f.fileId));

    try {
      await onUploadConversationFile(rejected.length ? toFileList(allowed) : fileList);
      const refreshed = await loadUploadedFiles();
      onUploadedFilesChanged?.();

      const newFile = refreshed.find((f) => !previousIds.has(f.fileId));
      if (newFile) {
        setSelectedUploadedFileIds((prev) =>
          prev.includes(newFile.fileId) ? prev : [...prev, newFile.fileId]
        );
        setJustUploaded({ id: newFile.fileId, name: newFile.fileName });
      }
    } catch {
      // Parent handler shows toast on failure.
    } finally {
      if (conversationUploadInputRef.current) {
        conversationUploadInputRef.current.value = '';
      }
    }
  };

  const openPortfolio = (portfolioId: string) => {
    setSelectedPortfolioId(portfolioId);
    setSelectedFolderId('');
    setExpanded((current) => new Set(current).add(`p:${portfolioId}`));
  };

  const openFolder = (portfolioId: string, folderId: string) => {
    setSelectedPortfolioId(portfolioId);
    setSelectedFolderId(folderId);
    setExpanded((current) => {
      const next = new Set(current);
      next.add(`p:${portfolioId}`);
      next.add(`f:${folderId}`);
      return next;
    });
  };

  const folderCrumbs = useMemo(() => {
    if (!selectedPortfolio || !selectedFolderId) return [];
    const crumbs: { id: string; title: string }[] = [];
    const walk = (folders: LibraryFolder[] | undefined): boolean => {
      for (const folder of folders ?? []) {
        if (folder.Id === selectedFolderId) {
          crumbs.push({ id: folder.Id, title: folder.Title });
          return true;
        }
        if (walk(folder.folders)) {
          crumbs.unshift({ id: folder.Id, title: folder.Title });
          return true;
        }
      }
      return false;
    };
    walk(selectedPortfolio.folders);
    return crumbs;
  }, [selectedPortfolio, selectedFolderId]);

  const visibleFolders = useMemo(() => {
    if (!selectedPortfolio) return [];
    if (!selectedFolderId) return selectedPortfolio.folders ?? [];
    return findLibraryFolder(selectedPortfolio.folders, selectedFolderId)?.folders ?? [];
  }, [selectedPortfolio, selectedFolderId]);

  const busy = saving || uploadingConversationFile || deletingFileId !== null || savingToLibrary;

  const renderUploadedSection = () => (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Uploaded files</h3>
        {selectedUploadedFileIds.length > 0 ? (
          <button
            type="button"
            className={styles.saveSelected}
            onClick={() => setSaveTargetIds(selectedUploadedFileIds)}
            disabled={busy}
          >
            Save selected to library ({selectedUploadedFileIds.length})
          </button>
        ) : uploadedFiles.length > 0 ? (
          <span className={styles.sectionCount}>{uploadedFiles.length} file{uploadedFiles.length === 1 ? '' : 's'}</span>
        ) : null}
      </div>

      {justUploaded && (
        <div className={styles.savePrompt}>
          <span>Uploaded {justUploaded.name}. Save it to your library?</span>
          <button
            type="button"
            onClick={() => setSaveTargetIds([justUploaded.id])}
            disabled={busy}
          >
            Choose folder
          </button>
        </div>
      )}

      {onUploadConversationFile && (
        <>
          <input
            ref={conversationUploadInputRef}
            type="file"
            className={styles.hiddenFileInput}
            accept={ALLOWED_UPLOAD_ACCEPT}
            onChange={(e) => handleConversationUpload(e.target.files)}
            disabled={busy}
          />
          <button
            type="button"
            className={`${styles.uploadZone} ${busy ? styles.uploadZoneDisabled : ''}`}
            onClick={() => conversationUploadInputRef.current?.click()}
            disabled={busy}
          >
            <img src="/img/document-upload.svg" alt="" className={styles.uploadZoneIcon} aria-hidden />
            <p className={styles.uploadZoneTitle}>
              {uploadingConversationFile ? 'Uploading…' : 'Upload a file'}
            </p>
            <p className={styles.uploadZoneHint}>Allowed: {ALLOWED_UPLOAD_LABEL}</p>
          </button>
        </>
      )}

      {loadingUploadedFiles ? (
        <div className={styles.loadingContainer}>
          <LoadingIcon />
        </div>
      ) : uploadedFilesError ? (
        <p className={styles.errorState} role="alert">
          {uploadedFilesError}
        </p>
      ) : uploadedFiles.length === 0 ? null : (
        <ul className={styles.fileList}>
          {uploadedFiles.map((file) => {
            const checked = selectedUploadedFileIds.includes(file.fileId);
            return (
              <li key={file.fileId} className={checked ? styles.fileRowSelected : undefined}>
                <label className={styles.fileOption}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleUploadedFileSelection(file.fileId)}
                    disabled={busy}
                  />
                  <img src={getFileIcon(file.fileName)} alt="" className={styles.fileTypeIcon} aria-hidden />
                  <span className={styles.fileOptionText}>
                    <span className={styles.fileOptionName}>{file.fileName}</span>
                    <span className={styles.fileOptionMeta}>
                      Uploaded {formatUploadDate(file.uploadedAt)}
                    </span>
                  </span>
                </label>
                <div className={styles.fileActions}>
                  <button
                    type="button"
                    className={styles.saveToLibrary}
                    onClick={() => setSaveTargetIds([file.fileId])}
                    disabled={busy}
                  >
                    Save to library
                  </button>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={() => handleDeleteUploadedFile(file.fileId, file.fileName)}
                    disabled={busy}
                    aria-label={`Remove ${file.fileName}`}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );

  const revealLinked = (file: LinkedEvidenceFile) => {
    const portfolio = portfolios.find((p) => p.id === file.portfolioId);
    if (!portfolio) return;
    const path = folderPath(portfolio.folders, file.folderId) ?? [];
    setSelectedPortfolioId(portfolio.id);
    setSelectedFolderId(file.folderId);
    setExpanded((current) => {
      const next = new Set(current);
      next.add(`p:${portfolio.id}`);
      path.forEach((id) => next.add(`f:${id}`));
      return next;
    });
    const key = libraryFileRefKey(file);
    setRevealKey(key);
    window.setTimeout(() => setRevealKey((current) => (current === key ? '' : current)), 1600);
  };

  const linkedKeyList = linkedEvidenceFiles.map(libraryFileRefKey);
  const allLinkedSelected =
    linkedKeyList.length > 0 && linkedKeyList.every((key) => selectedFileKeys.includes(key));

  const renderLinkedEvidenceSection = () => (
    <section className={styles.linkedStrip}>
      <div className={styles.linkedStripHeader}>
        <span className={styles.linkedStripTitle}>
          Already linked
          <span className={styles.linkedStripCount}>{linkedEvidenceFiles.length}</span>
        </span>
        {!portfolioFilesOnly && linkedEvidenceFiles.length > 0 && (
          <button
            type="button"
            className={styles.linkedStripAction}
            onClick={() =>
              setSelectedFileKeys((prev) =>
                allLinkedSelected
                  ? prev.filter((key) => !linkedKeys.has(key))
                  : Array.from(new Set([...prev, ...linkedKeyList]))
              )
            }
            disabled={busy}
          >
            {allLinkedSelected ? 'Clear linked selection' : 'Use all linked files'}
          </button>
        )}
      </div>
      {linkedEvidenceFiles.length === 0 ? (
        <p className={styles.linkedStripEmpty}>
          Nothing is linked to this {subjectNoun} yet. Pick files from your portfolios below.
        </p>
      ) : (
        <div className={styles.linkedChips}>
          {linkedEvidenceFiles.map((file) => {
            const key = libraryFileRefKey(file);
            const selected = !portfolioFilesOnly && selectedFileKeys.includes(key);
            const location = [file.portfolioTitle, file.folderTitle].filter(Boolean).join(' / ');
            return (
              <button
                key={key}
                type="button"
                className={`${styles.linkedChip} ${selected ? styles.linkedChipSelected : ''}`}
                onClick={() => revealLinked(file)}
                title={location ? `${file.fileName}\n${location}` : file.fileName}
              >
                <img src={getFileIcon(file.fileName)} alt="" className={styles.linkedChipIcon} aria-hidden />
                <span className={styles.linkedChipName}>{file.fileName}</span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );

  const renderLibrarySection = () => (
    <section className={styles.section}>
      {loadingPortfolios ? (
        <div className={styles.loadingContainer}>
          <LoadingIcon />
        </div>
      ) : loadError ? (
        <p className={styles.errorState} role="alert">
          {loadError}
        </p>
      ) : portfolios.length === 0 ? (
        <p className={styles.emptyFiles}>
          No library portfolios found. Add files in the Library first.
        </p>
      ) : (
        <div className={styles.explorer}>
          <LibraryExplorerTree
            portfolios={portfolios}
            expanded={expanded}
            setExpanded={setExpanded}
            selectedPortfolioId={selectedPortfolioId}
            selectedFolderId={selectedFolderId}
            onOpenPortfolio={openPortfolio}
            onOpenFolder={openFolder}
          />
          <div className={styles.explorerMain}>
            <div className={styles.crumbs}>
              <button type="button" onClick={() => selectedPortfolio && openPortfolio(selectedPortfolio.id)}>
                {selectedPortfolio?.title ?? 'Portfolio'}
              </button>
              {folderCrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.id}>
                  <ChevronIcon size={12} />
                  {index === folderCrumbs.length - 1 ? (
                    <span>{crumb.title}</span>
                  ) : (
                    <button type="button" onClick={() => selectedPortfolio && openFolder(selectedPortfolio.id, crumb.id)}>
                      {crumb.title}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>
            {visibleFolders.length === 0 && visibleFiles.length === 0 ? (
              <p className={styles.emptyFiles}>
                {selectedFolderId ? 'This folder is empty.' : 'This portfolio has no folders yet.'}
              </p>
            ) : (
              <ul className={styles.explorerList}>
                {visibleFolders.map((folder) => (
                  <li key={folder.Id}>
                    <button
                      type="button"
                      className={styles.explorerFolder}
                      onClick={() => selectedPortfolio && openFolder(selectedPortfolio.id, folder.Id)}
                    >
                      <span className={styles.folderGlyph}><FolderIcon size={16} /></span>
                      <span>{folder.Title}</span>
                    </button>
                  </li>
                ))}
                {visibleFiles.map((file) => {
                  const key = libraryFileRefKey(file);
                  const locked = portfolioFilesOnly && file.alreadyLinked;
                  const checked = locked || selectedFileKeys.includes(key);
                  return (
                    <li key={key}>
                      <label
                        className={[
                          styles.explorerFile,
                          checked && !locked ? styles.explorerFileSelected : '',
                          locked ? styles.explorerFileLocked : '',
                          revealKey === key ? styles.explorerFileReveal : '',
                        ].filter(Boolean).join(' ')}
                        title={locked ? `Already linked to this ${subjectNoun}` : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleFileSelection(key)}
                          disabled={busy || locked}
                        />
                        <img src={getFileIcon(file.fileName)} alt="" className={styles.fileTypeIcon} aria-hidden />
                        <span className={styles.explorerFileName}>{file.fileName}</span>
                        {file.alreadyLinked && <span className={styles.linkedTag}>Linked</span>}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <img src="/img/sparkle-blue.svg" alt="" className={styles.sparkleIcon} aria-hidden />
          <h2 className={styles.title}>{portfolioFilesOnly ? 'Link portfolio files' : 'Add evidence'}</h2>
        </div>
        {contextLabel && <p className={styles.subtitle}>For: {contextLabel}</p>}
      </div>

      {!portfolioFilesOnly && renderUploadedSection()}
      {renderLinkedEvidenceSection()}
      {renderLibrarySection()}

      <div className={styles.footer}>
        <p className={styles.footerHint}>
          {totalSelected > 0
            ? `${totalSelected} file${totalSelected === 1 ? '' : 's'} selected`
            : portfolioFilesOnly
              ? 'Select portfolio files to link'
              : 'Select files or upload new evidence'}
        </p>
        <div className={styles.footerActions}>
          <Button
            size="Small"
            colour="#FFF"
            textColour="#000"
            border="rgba(0,0,0,0.15)"
            clickHandler={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            colour="#24394F"
            clickHandler={handleAddEvidence}
            disabled={busy || totalSelected === 0}
          >
            {portfolioFilesOnly
              ? saving
                ? 'Linking…'
                : `Link ${totalSelected > 0 ? `${totalSelected} ` : ''}file${totalSelected === 1 ? '' : 's'}`
              : saving
                ? 'Adding…'
                : `Add evidence${totalSelected > 0 ? ` (${totalSelected})` : ''}`}
          </Button>
        </div>
      </div>
      {saveTargetIds.length > 0 && (
        <Popup
          isVisible
          zIndex={100001}
          onClose={() => {
            if (!savingToLibrary) setSaveTargetIds([]);
          }}
        >
          <LibraryFolderPicker
            portfolios={portfolios}
            saving={savingToLibrary}
            fileCount={saveTargetIds.length}
            onCancel={() => setSaveTargetIds([])}
            onConfirm={confirmSaveToLibrary}
          />
        </Popup>
      )}
    </div>
  );
};

export default AddEvidencePopup;
